import {
  Box,
  Card,
  Typography,
  Tabs,
  Tab,
  Avatar,
  Stack,
  Divider,
  useMediaQuery,
  useTheme,
  Chip,
} from "@mui/material";
import { useEffect } from "react";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { getUserDetails } from "../../services/api/user";
import { AccountInformation } from "./AccountInformation";
import { SubscriptionDetails } from "./SubscriptionDetails";
import { PaymentHistory } from "./PaymentHistory";
import { getSubscriptionDetailsByUserId } from "../../services/api/subscription";

const UserProfile = () => {
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);
  const [userDetails, setUserDetails] = useState([]);
  const [planDetails, setPlanDetails] = useState([]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const user_response = await getUserDetails();
        const plan_response = await getSubscriptionDetailsByUserId();
        setUserDetails(user_response.data);
        setPlanDetails(plan_response.data);
      } catch (error) {
        dispatch(showToast({ message: error.message, severity: "error" }));
      }
    };

    fetchUserDetails();
  }, [dispatch]);

  const firstNameInitial = userDetails?.first_name?.charAt(0) || "NA";

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 8,
          p: 4,
        }}
      >
        {/* Left Profile Card */}
        <Card
          sx={{
            flex: "0 0 400px", // Ensures the card stays fixed width
            textAlign: "center",
            p: 8,
            borderRadius: 3,
            boxShadow: 3,
            height: "60vh", // Make the left card take the full height of the screen
            position: "sticky", // Keep the card fixed
            top: 0,
          }}
        >
          <Avatar
            sx={{
              width: 80,
              height: 80,
              bgcolor: "primary.main",
              fontSize: 32,
              mx: "auto",
              mb: 2,
            }}
          >
            {firstNameInitial}
          </Avatar>
          <Typography variant="h5" fontWeight={600}>
            {`${userDetails.first_name} ${userDetails.last_name}`}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            {userDetails?.company ? userDetails.company : "-"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {userDetails?.address ? userDetails.address : "-"}
          </Typography>

          {userDetails.is_active && (
            <Chip
              label={userDetails.is_active ? "Active" : "Suspended"}
              color={userDetails.is_active ? "success" : "error"}
              size="small"
              sx={{ mt: 1 }}
            />
          )}

          <Divider sx={{ my: 2 }} />

          <Stack spacing={1}>
            <Typography variant="subtitle2" color="text.secondary">
              Contact
            </Typography>
            <Typography variant="body2">📞 {userDetails.contact}</Typography>
            <Typography variant="body2">✉️ {userDetails.email}</Typography>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Subscription
            </Typography>
            <Typography variant="body1">{`Plan: ${userDetails.plan_name}`}</Typography>
            <Typography variant="caption" color="text.secondary">
              {`Renews: ${new Date(userDetails.end_date).toLocaleDateString(
                "en-GB"
              )}`}
            </Typography>
          </Box>
        </Card>

        {/* Right Section - Scrollable Tab Content */}
        <Box
          sx={{
            flex: 2,
            maxHeight: "100vh", // Full viewport height
            overflowY: "auto", // Enable vertical scrolling
            paddingRight: 2, // Space for scroll bar
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            textColor="primary"
            indicatorColor="primary"
            variant={isMobile ? "scrollable" : "standard"}
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label="Account Information" />
            <Tab label="Subscription Details" />
            <Tab label="Payment History" />
          </Tabs>

          {/* Account Information */}
          <TabPanel value={tabValue} index={0}>
            <AccountInformation userDetails={userDetails} />
          </TabPanel>

          {/* Subscription Details */}
          <TabPanel value={tabValue} index={1}>
            <SubscriptionDetails planDetails={planDetails} />
          </TabPanel>

          {/* Payment History */}
          <TabPanel value={tabValue} index={2}>
            <PaymentHistory userDetails={userDetails} />
          </TabPanel>
        </Box>
      </Box>
    </>
  );
};

// Reusable TabPanel component
const TabPanel = ({ children, value, index }) => {
  return value === index ? <div>{children}</div> : null;
};

export default UserProfile;
